// Helpers de agregación por cliente para ClientView e IndicatorsView.
//
// Convenciones:
//   - "Documentos del cliente" son los que tienen f.metadata.cliente.
//   - "Placeholder" se detecta por nombre que contiene "placeholder".
//   - Para secciones noJMA (Verificación UVA, Acuses Regulatorios), la
//     existencia de cualquier documento NO-placeholder se considera
//     suficiente para marcarla en verde, ya que JMA no es el productor.

import { AFAC_SECCIONES, FASES } from './afac-sections.js'
import { getDocumentEntry } from './validation-store.js'

function isPlaceholder(file) {
  return /placeholder/i.test(file?.name || '')
}

function isAprobado(file) {
  return getDocumentEntry(file)?.estado === 'Aprobado'
}

export function detectarClientes(allFiles) {
  const set = new Set()
  for (const f of allFiles) {
    if (f.metadata?.cliente) set.add(f.metadata.cliente)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'))
}

export function getDocsForClient(allFiles, clienteId) {
  if (!clienteId) return []
  return allFiles.filter((f) => f.metadata?.cliente === clienteId)
}

export function aggregateClient(clientFiles) {
  const porFase = {}
  for (const fase of FASES) porFase[`Fase ${fase.numero}`] = []
  for (const f of clientFiles) {
    const fase = f.metadata?.fase
    if (fase && porFase[fase]) porFase[fase].push(f)
  }

  const porSeccion = {}
  for (const sec of AFAC_SECCIONES) porSeccion[sec.numero] = []
  for (const f of clientFiles) {
    const sec = f.metadata?.seccionAFAC
    if (sec != null && porSeccion[sec]) porSeccion[sec].push(f)
  }

  return { porFase, porSeccion, total: clientFiles.length }
}

/**
 * Calcula el estado de una sección de la Carpeta AFAC para un cliente.
 *
 * Estados:
 *   - 'verde'  : completa.
 *   - 'ambar'  : parcial (sólo aplica a secciones JMA).
 *   - 'rojo'   : sin documentos o sólo placeholders (sólo secciones JMA).
 *   - 'externa': sección noJMA pendiente de entrega de la UVA / autoridad.
 *                Diferencia importante respecto a 'rojo': no es un fallo
 *                de JMA, es un entregable de terceros pendiente.
 *
 * @returns {{ estado: 'verde'|'ambar'|'rojo'|'externa', mensaje: string, total: number, aprobados: number }}
 */
export function computeSectionStatus(docs, opts = {}) {
  const noJMA = !!opts.noJMA
  const noPlaceholders = (docs || []).filter((d) => !isPlaceholder(d))

  if (noJMA) {
    if (noPlaceholders.length === 0) {
      return {
        estado: 'externa',
        mensaje: 'Pendiente entrega externa',
        total: 0,
        aprobados: 0
      }
    }
    return {
      estado: 'verde',
      mensaje: `${noPlaceholders.length} entregable(s) recibidos de terceros`,
      total: noPlaceholders.length,
      aprobados: noPlaceholders.length
    }
  }

  if (!docs || docs.length === 0) {
    return { estado: 'rojo', mensaje: 'Sin documentos', total: 0, aprobados: 0 }
  }
  if (noPlaceholders.length === 0) {
    return {
      estado: 'rojo',
      mensaje: 'Solo placeholders',
      total: docs.length,
      aprobados: 0
    }
  }
  const aprobados = noPlaceholders.filter(isAprobado).length
  if (aprobados === noPlaceholders.length) {
    return {
      estado: 'verde',
      mensaje: 'Todos aprobados',
      total: noPlaceholders.length,
      aprobados
    }
  }
  return {
    estado: 'ambar',
    mensaje: `${aprobados}/${noPlaceholders.length} aprobados`,
    total: noPlaceholders.length,
    aprobados
  }
}

export function computeAllSections(porSeccion) {
  return AFAC_SECCIONES.map((sec) => {
    const docs = porSeccion[sec.numero] || []
    const status = computeSectionStatus(docs, { noJMA: sec.noJMA })
    return { ...sec, docs, ...status }
  })
}

export function estadoFase(docs) {
  if (!docs || docs.length === 0) return 'no_iniciada'
  const aprobados = docs.filter(isAprobado).length
  if (aprobados === docs.length) return 'cerrada'
  return 'en_curso'
}

export function detectarFaseActiva(porFase) {
  let lastConDocs = null
  for (let i = 0; i <= 6; i++) {
    const key = `Fase ${i}`
    const docs = porFase[key] || []
    if (docs.length === 0) continue
    lastConDocs = key
    const aprobados = docs.filter(isAprobado).length
    if (aprobados < docs.length) return key
  }
  return lastConDocs || 'Fase 0'
}

export function countsByEstado(files) {
  const counts = {
    Aprobado: 0,
    Pendiente: 0,
    'Requiere ajuste': 0,
    Bloqueado: 0,
    Obsoleto: 0,
    'Sin estado': 0
  }
  for (const f of files) {
    const estado = getDocumentEntry(f)?.estado || 'Sin estado'
    if (counts[estado] != null) counts[estado] += 1
    else counts['Sin estado'] += 1
  }
  return counts
}

export function percentByFase(files) {
  const data = {}
  for (let i = 0; i <= 6; i++) {
    const key = `Fase ${i}`
    const docs = files.filter((f) => f.metadata?.fase === key)
    if (docs.length === 0) {
      data[key] = { total: 0, aprobados: 0, porcentaje: 0 }
      continue
    }
    const aprobados = docs.filter(isAprobado).length
    data[key] = {
      total: docs.length,
      aprobados,
      porcentaje: Math.round((aprobados / docs.length) * 100)
    }
  }
  return data
}

export function countsByAudiencia(files) {
  const c = {}
  for (const f of files) {
    const aud = f.metadata?.audiencia || 'Sin clasificar'
    c[aud] = (c[aud] || 0) + 1
  }
  return c
}
